/**
 * The header's + button: start the records people create most. It opens on hover (mouse, after a
 * short intent delay, closing when the pointer leaves), and like any menu on click, Enter, Space or
 * the arrow keys, so hover is never the only way in (WCAG 1.4.13, 2.1.1).
 */
import { Plus } from "lucide-react";
import { DropdownMenu as M } from "radix-ui";
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { useNavigate } from "react-router";

import type { QuickCreate } from "@/app/quickCreate";
import { cx } from "@/lib/cx";

const OPEN_DELAY = 80;
const CLOSE_DELAY = 220;

export function QuickCreateMenu({ actions, compact = false }: { actions: QuickCreate[]; compact?: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const hoverOpened = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  if (actions.length === 0) return null;

  const isMouse = (e: PointerEvent) => e.pointerType === "mouse";
  const hoverIn = (e: PointerEvent) => {
    if (!isMouse(e)) return;
    clearTimeout(timer.current);
    if (!open)
      timer.current = setTimeout(() => {
        hoverOpened.current = true;
        setOpen(true);
      }, OPEN_DELAY);
  };
  const hoverOut = (e: PointerEvent) => {
    if (!isMouse(e)) return;
    clearTimeout(timer.current);
    // Only a menu the pointer opened closes when it leaves; one opened on purpose stays.
    if (hoverOpened.current) timer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  };

  return (
    <M.Root
      open={open}
      // Non-modal: the pointer can travel from the button to the menu, and the page stays usable.
      modal={false}
      onOpenChange={(next) => {
        clearTimeout(timer.current);
        hoverOpened.current = false;
        setOpen(next);
      }}
    >
      <M.Trigger asChild>
        <button
          type="button"
          aria-label="Create"
          data-tour="quick-create"
          onPointerEnter={hoverIn}
          onPointerLeave={hoverOut}
          onPointerDown={(e) => {
            // A click on a button whose menu the hover already opened keeps it open (and makes it stay).
            if (open && hoverOpened.current && isMouse(e)) {
              e.preventDefault();
              hoverOpened.current = false;
            }
          }}
          className={cx(
            "flex items-center justify-center gap-1 rounded-md bg-accent text-sm font-medium text-accent-fg transition-colors transition-fast hover:bg-accent-hover",
            "data-[state=open]:bg-accent-hover",
            compact ? "size-8" : "h-7 px-2",
          )}
        >
          <Plus aria-hidden className="size-4" />
          {!compact && <span>New</span>}
        </button>
      </M.Trigger>
      <M.Portal>
        <M.Content
          align="end"
          sideOffset={6}
          aria-label="Create"
          onPointerEnter={hoverIn}
          onPointerLeave={hoverOut}
          // A menu the pointer opened returns no focus to the button when the pointer leaves.
          onCloseAutoFocus={(e) => hoverOpened.current && e.preventDefault()}
          className="animate-pop-in z-50 min-w-52 rounded-md border border-line bg-surface-raised p-1 shadow-md"
        >
          {actions.map(({ key, label, to, icon: Icon }) => (
            <M.Item
              key={key}
              onSelect={() => navigate(to)}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-fg outline-none select-none data-[highlighted]:bg-surface-hover"
            >
              <Icon aria-hidden className="size-4 text-fg-subtle" />
              {label}
            </M.Item>
          ))}
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}
